import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const AdmissionContactsTable = ({ contacts, childId, axiosPrivate, onUpdateSuccess, canEdit }) => {
  const [localContacts, setLocalContacts] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedContact, setEditedContact] = useState({});
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setLocalContacts(contacts);
  }, [contacts]);

  const columnLabels = [
    t("studentDetails.contactsCard.name"),
    t("studentDetails.contactsCard.email"),
    t("studentDetails.contactsCard.phone"),
    t("studentDetails.contactsCard.canPickUp"),
    t("studentDetails.contactsCard.signedUp"), 
    t("studentDetails.contactsCard.contactType"),
    t("studentDetails.contactsCard.contactStatus"),
    t("actions.actions"),
  ];

  const handleEditClick = (index) => {
    setEditingIndex(index);
    const contact = localContacts[index];
    setEditedContact({
      ...contact,
      name: `${contact.FirstName} ${contact.LastName}`.trim(),
    });
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedContact({});
  };

  const handleChange = (field, value) => {
    setEditedContact((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const contactId = localContacts[editingIndex].id;

      
      const [FirstName, ...lastNameParts] = editedContact.name.split(" ");
      const LastName = lastNameParts.join(" ");

      const updateData = {
        FirstName: FirstName || "",
        LastName: LastName || "",
        Email: editedContact.Email,
        Phone: editedContact.Phone,
        CanPickUp: editedContact.CanPickUp,
        SignedUp: editedContact.SignedUp,
        // ContactType & Status IDs
        ContactType: editedContact.ContactType,
        ContactStatus: editedContact.ContactStatus,
      };

      await axiosPrivate.patch(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${childId}/contact/${contactId}/`,
        updateData
      );

      toast.success(i18n.language === "ar" ? "تم حفظ التعديلات" : "Changes saved");
      setEditingIndex(null);
      onUpdateSuccess(true);
    } catch (err) {
      toast.error(i18n.language === "ar" ? "فشل في تحديث جهة الاتصال" : "Failed to update contact.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "white", overflowX: "auto", mt: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f2f2f2", borderBottom: "1px solid #f0f0f0", px: 2, py: 1 }}>
        <Typography variant="header" fontWeight="bold" color="#333">
          {t("studentDetails.contactsCard.title")}
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {columnLabels.map((label) => (
                <TableCell
                  key={label}
                  align={i18n.dir() === "rtl" ? "right" : "left"}
                  sx={{ fontWeight: 600, fontSize: "medium", borderBottom: "1px solid #ccc", px: 1 }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {localContacts.map((contact, i) => (
              <TableRow key={contact.id} hover sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
                {/* Name Field (Merged) */}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar sx={{ bgcolor: "#d0d0d0", width: 34, height: 34, color: "black", fontWeight: "bold", fontSize: "0.9rem" }}>
                      {contact.FirstName?.charAt(0)}{contact.LastName?.charAt(0)}
                    </Avatar>
                    {editingIndex === i ? (
                      <TextField size="small" value={editedContact.name || ""} onChange={(e) => handleChange("name", e.target.value)} />
                    ) : (
                      <Typography>{`${contact.FirstName} ${contact.LastName}`}</Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Email & Phone */}
                {["Email", "Phone"].map((field) => (
                  <TableCell key={field}>
                    {editingIndex === i ? (
                      <TextField size="small" value={editedContact[field] || ""} onChange={(e) => handleChange(field, e.target.value)} />
                    ) : (
                      contact[field] || "—"
                    )}
                  </TableCell>
                ))}

                {/* Boolean Selects (Can Pick Up & Signed Up) */}
                {["CanPickUp", "SignedUp"].map((field) => (
                  <TableCell key={field}>
                    {editingIndex === i ? (
                      <Select size="small" value={String(editedContact[field])} onChange={(e) => handleChange(field, e.target.value === "true")}>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    ) : contact[field] ? "Yes" : "No"}
                  </TableCell>
                ))}

                {/* Type & Status */}
                <TableCell>{contact.ContactType_name || "—"}</TableCell>
                <TableCell>{contact.ContactStatus_name || "—"}</TableCell>

                {/* Actions  */}
                <TableCell sx={{ px: 0 }}>
                  {canEdit && (
                    editingIndex === i ? (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" sx={{ color: "#702E8F", textTransform: "none" }} onClick={handleSave} disabled={loading}>
                          {t("actions.save")}
                        </Button>
                        <Button size="small" sx={{ color: "#dc3545", textTransform: "none" }} onClick={handleCancel}>
                          {t("actions.cancel")}
                        </Button>
                      </Box>
                    ) : (
                      <Button size="small" sx={{ color: "#702E8F", textTransform: "none" }} onClick={() => handleEditClick(i)}>
                        {t("actions.edit")}
                      </Button>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default AdmissionContactsTable;